import {useSearchParams} from 'react-router-dom';
import type {RouterInput} from '@common-web/lib/trpc';

export type ConsolidatedFiltersInput = NonNullable<
  RouterInput['internal']['claimsTracking']['consolidated']['getOverview']
>;

function parseList(param: string | null): string[] {
  return param ? param.split(',').filter(Boolean) : [];
}

function serializeList(values: string[]): string | null {
  return values.length ? values.join(',') : null;
}

/**
 * Reads the shared claims filters from the URL so every consolidated tab pulls
 * the same slice of the universe.
 */
export function useConsolidatedFilters() {
  const [searchParams, setSearchParams] = useSearchParams();

  const groupId = searchParams.get('group') || undefined;
  const planId = searchParams.get('plan') || undefined;
  const stage = (searchParams.get('stage') || undefined) as
    | ConsolidatedFiltersInput['stage']
    | undefined;
  const status = parseList(searchParams.get('status'));
  const search = searchParams.get('search') || undefined;
  const exCode = parseList(searchParams.get('exCode'));
  const errType = searchParams.get('errType') || undefined;

  const filters: ConsolidatedFiltersInput = {
    groupId,
    planId,
    stage,
    status: status.length ? status : undefined,
    search,
    exCode: exCode.length ? exCode : undefined,
    errType,
  };

  const updateParams = (updates: Record<string, string | null>) => {
    setSearchParams((prev) => {
      for (const [key, value] of Object.entries(updates)) {
        if (value === null) prev.delete(key);
        else prev.set(key, value);
      }
      return prev;
    });
  };

  const setStatus = (values: string[]) =>
    updateParams({status: serializeList(values)});

  const setExCode = (values: string[]) =>
    updateParams({exCode: serializeList(values)});

  /** Navigate to the Claims tab with additional filter params applied. */
  const drillToClaims = (extra: Record<string, string | null>) => {
    updateParams({tab: 'claims', ...extra});
  };

  const hasActiveFilters = Boolean(
    groupId || planId || stage || status.length || search || exCode.length || errType,
  );

  const clearFilters = () =>
    updateParams({
      group: null,
      plan: null,
      stage: null,
      status: null,
      search: null,
      exCode: null,
      errType: null,
    });

  return {
    filters,
    groupId,
    planId,
    stage,
    status,
    search: search ?? '',
    exCode,
    errType,
    hasActiveFilters,
    updateParams,
    setStatus,
    setExCode,
    drillToClaims,
    clearFilters,
  };
}
